/**
 * AgentActivityFeed Component
 * AIDLC Administrative Backend
 *
 * Live activity feed for an agent profile:
 *   - Active task banner (source, subject, elapsed timer, pulsing dot)
 *   - Terminal-style scrollable output feed with auto-scroll
 *   - Line numbers, color-coded by type (stdout=gray, stderr=yellow, system=blue)
 */

import { useEffect, useRef, useState } from 'react';
import { useAgentActivity } from '../../hooks/useAgentActivity';

interface AgentActivityFeedProps {
  agentId: string;
  agentName: string;
}

const sourceLabels: Record<string, string> = {
  chat: 'Chat Message',
  'go-do-it': 'Go Do It',
  'task-tool': 'Task Tool',
  manual: 'Manual',
};

function ElapsedTimer({ startedAt }: { startedAt: string }) {
  const [elapsed, setElapsed] = useState('0s');

  useEffect(() => {
    function update() {
      const diff = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
      if (diff < 60) {
        setElapsed(`${diff}s`);
      } else if (diff < 3600) {
        const m = Math.floor(diff / 60);
        const s = diff % 60;
        setElapsed(`${m}m ${s}s`);
      } else {
        const h = Math.floor(diff / 3600);
        const m = Math.floor((diff % 3600) / 60);
        setElapsed(`${h}h ${m}m`);
      }
    }
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  return <span className="font-mono text-xs">{elapsed}</span>;
}

export function AgentActivityFeed({ agentId, agentName }: AgentActivityFeedProps) {
  const { activeTask, lines, isLoading, error, restricted } = useAgentActivity(agentId);
  const feedRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll to bottom when new lines arrive
  useEffect(() => {
    if (autoScroll && feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [lines, autoScroll]);

  // Detect manual scroll to disable auto-scroll
  function handleScroll() {
    if (!feedRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = feedRef.current;
    // If within 40px of bottom, re-enable auto-scroll
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 40);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-5 h-5 border-2 border-ldaja-blue border-t-transparent rounded-full" />
        <span className="ml-2 text-sm text-gray-500">Loading activity...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Active Task Banner */}
      {activeTask ? (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* Pulsing dot */}
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
              </span>
              <span className="text-sm font-medium text-green-800">
                {agentName} is working
              </span>
            </div>
            <ElapsedTimer startedAt={activeTask.startedAt} />
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-medium rounded">
              {sourceLabels[activeTask.source] || activeTask.source}
            </span>
            <span className="text-sm text-green-700 truncate">{activeTask.subject}</span>
          </div>
        </div>
      ) : (
        <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-gray-300" />
            <span className="text-sm text-gray-500">No active task</span>
          </div>
        </div>
      )}

      {error && (
        <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {agentName}&apos;s activity could not be loaded. Try again in a moment.
        </div>
      )}

      {restricted ? (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Output Feed</h4>
          <p className="text-sm text-gray-600">
            Agent output is shown to Admins only, because it can include data the agent read. Ask an Admin if you need it.
          </p>
        </div>
      ) : (
      /* Terminal-style output feed */
      <div className="relative">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Output Feed</h4>
          <div className="flex items-center gap-2">
            {!autoScroll && lines.length > 0 && (
              <button
                onClick={() => {
                  setAutoScroll(true);
                  if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
                }}
                className="text-[10px] text-ldaja-blue hover:underline"
              >
                Scroll to bottom
              </button>
            )}
            <span className="text-[10px] text-gray-400">{lines.length} lines</span>
          </div>
        </div>

        <div
          ref={feedRef}
          onScroll={handleScroll}
          className="bg-gray-900 rounded-lg p-3 h-[400px] overflow-y-auto font-mono text-xs leading-5 scroll-smooth"
        >
          {lines.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">
                {error
                  ? 'Output could not be loaded.'
                  : activeTask
                  ? 'Waiting for output...'
                  : `No activity recorded. Send ${agentName} a task to see output here.`}
              </p>
            </div>
          ) : (
            lines.map((l, i) => (
              <div key={`${l.lineNumber}-${i}`} className="flex hover:bg-gray-800/50 px-1 rounded">
                {/* Line number */}
                <span className="w-10 flex-shrink-0 text-gray-600 text-right mr-3 select-none">
                  {l.lineNumber}
                </span>
                {/* Line content */}
                <span
                  className={
                    l.line.startsWith('[Governance]')
                      ? 'text-purple-400'
                      : l.lineType === 'stderr'
                        ? 'text-yellow-400'
                        : l.lineType === 'system'
                          ? 'text-blue-400'
                          : 'text-gray-300'
                  }
                >
                  {l.line}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
      )}
    </div>
  );
}
